import React from 'react'
import { useNavigate } from 'react-router-dom'
import type { FC, ReactNode, MouseEvent } from 'react'

interface CustomLinkProps {
  to: string
  children: ReactNode
  className?: string
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void
  target?: string
  rel?: string
}

/**
 * Custom Link component that uses useNavigate hook for reliable navigation
 * This fixes issues with React Router Link components not working properly
 */
const CustomLink: FC<CustomLinkProps> = ({
  to,
  children,
  className = '',
  onClick,
  target,
  rel
}) => {
  const navigate = useNavigate()

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    // If it's an external link or has target="_blank", let it behave normally
    if (target === '_blank' || to.startsWith('http') || to.startsWith('//')) {
      if (onClick) onClick(event)
      return
    }

    // Prevent default link behavior for internal navigation
    event.preventDefault()

    // Call custom onClick if provided
    if (onClick) {
      onClick(event)
    }

    // Navigate using React Router
    try {
      navigate(to)
    } catch (error) {
      console.error('Navigation error:', error)
      // Fallback to window.location if navigate fails
      window.location.href = to
    }
  }

  return (
    <a
      href={to}
      className={className}
      onClick={handleClick}
      target={target}
      rel={rel}
    >
      {children}
    </a>
  )
}

export default CustomLink